import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { JSX, PointerEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    BODY_DIAGRAM_HEIGHT,
    BODY_DIAGRAM_INK,
    BODY_DIAGRAM_OUTLINE_SRC,
    BODY_DIAGRAM_STROKE_WIDTH,
    BODY_DIAGRAM_WIDTH,
    clampToDiagram,
    drawStrokes,
    Stroke,
} from '../../library/bodyDiagramHelper';
import { canvasPoint, midpoint, Point } from '../../library/signatureHelper';
import ResourcesIds from '../../resources/ResourcesIds';
import {
    canvasStyles,
    dialogActionsStyles,
    dialogTitleStyles,
    outlineImageStyles,
    spacerStyles,
    stageAreaStyles,
    stageStyles,
} from './bodyDiagramDialog.styles';

interface IBodyDiagramDialogProps {
    open: boolean;
    /** What is already drawn. The dialog edits a copy; nothing changes until Done. */
    strokes: Stroke[];
    onCancel: () => void;
    onDone: (strokes: Stroke[]) => void;
}

/**
 * The backing store is a fixed multiple of the artwork rather than of the screen, so the
 * ink is equally sharp on a phone and a tablet and the maths never depends on the device.
 */
const CANVAS_SCALE = 2;

/**
 * Where the therapist marks where it hurts: the body outline, full screen, with a pen.
 *
 * <p>Full screen always, not only on a compact screen, because the size of the surface is the
 * whole point -- the diagram in the form is a 240px thumbnail, and nobody can mark a knee on
 * that with a finger.
 *
 * <p>It shares the signature pad's pointer handling and its reasons (see `SignaturePad`),
 * plus two of its own:
 *
 * <ul>
 *   <li><b>One pointer at a time.</b> A palm resting on a tablet, or a second finger, is a
 *       second pointer; without this it would draw a second line alongside the first.</li>
 *   <li><b>Once a stylus has been used, touch is ignored.</b> With a pencil in hand the palm
 *       is on the glass, and it is the palm that would otherwise be drawing.</li>
 * </ul>
 *
 * <p>Strokes are recorded in the artwork's own 800x1013 pixels (see `bodyDiagramHelper`), so
 * the drawing survives the dialog being reopened at another size, a rotation, and export.
 */
export const BodyDiagramDialog = ({ open, strokes, onCancel, onDone }: IBodyDiagramDialogProps): JSX.Element => {
    const { t: translate } = useTranslation();

    return (
        <Dialog open={open} onClose={onCancel} fullScreen>
            <DialogTitle sx={dialogTitleStyles}>
                {translate(ResourcesIds.EVALUATION_BODY_DIAGRAM_DIALOG_TITLE)}
            </DialogTitle>

            {/* Mounted only while open, so every opening starts from the value it was given
                and a cancelled edit leaves nothing behind for the next one. */}
            {open && <DrawingSurface initial={strokes} onCancel={onCancel} onDone={onDone} />}
        </Dialog>
    );
};

interface IDrawingSurfaceProps {
    initial: Stroke[];
    onCancel: () => void;
    onDone: (strokes: Stroke[]) => void;
}

const DrawingSurface = ({ initial, onCancel, onDone }: IDrawingSurfaceProps): JSX.Element => {
    const { t: translate } = useTranslation();

    const areaRef = useRef<HTMLDivElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    const [strokes, setStrokes] = useState<Stroke[]>(initial);
    const [stage, setStage] = useState<{ width: number; height: number } | null>(null);

    /** The stroke under the pen right now, and the pointer drawing it. */
    const currentRef = useRef<Stroke | null>(null);
    const pointerRef = useRef<number | undefined>(undefined);
    const penSeenRef = useRef<boolean>(false);

    /**
     * The largest box at the artwork's proportions that fits the space left over. Measured,
     * not left to CSS: `aspect-ratio` gives way the moment both a height and a max-width
     * bind, and a stage that is not exactly 800:1013 puts ink beside where the pen was.
     */
    const measure = useCallback((): void => {
        const area: HTMLDivElement | null = areaRef.current;

        if (!area) {
            return;
        }

        const box: DOMRect = area.getBoundingClientRect();

        // Zero under jsdom, and for a moment while the dialog is still opening.
        if (box.width === 0 || box.height === 0) {
            return;
        }

        // The rect is the border box, so the area's own padding has to come off it. Left in,
        // the stage is sized to a space larger than the one it sits in and rides up over the
        // hint above it -- which is the padding failing to do the one thing it was added for.
        const style: CSSStyleDeclaration = window.getComputedStyle(area);
        const free = (extent: number, ...sides: string[]): number =>
            extent - sides.reduce((total, side) => total + parseFloat(style.getPropertyValue(side) || '0'), 0);

        const ratio: number = BODY_DIAGRAM_WIDTH / BODY_DIAGRAM_HEIGHT;
        const room = {
            width: free(box.width, 'padding-left', 'padding-right'),
            height: free(box.height, 'padding-top', 'padding-bottom'),
        };
        const width: number = Math.floor(Math.min(room.width, room.height * ratio));

        setStage({ width, height: Math.floor(width / ratio) });
    }, []);

    useLayoutEffect(() => {
        measure();
    }, [measure]);

    useEffect(() => {
        window.addEventListener('resize', measure);
        window.addEventListener('orientationchange', measure);

        return (): void => {
            window.removeEventListener('resize', measure);
            window.removeEventListener('orientationchange', measure);
        };
    }, [measure]);

    // The committed drawing, redrawn whole whenever it changes -- after a stroke ends, an
    // undo or a clear.
    //
    // The backing store is sized here rather than through `width`/`height` on the element:
    // MUI's Box treats both as *style* props and turns them into CSS, so the canvas keeps
    // its default 300x150 store and every stroke lands somewhere other than where the pen
    // was. The same is true of the thumbnail in `BodyDiagramField`.
    useEffect(() => {
        const canvas: HTMLCanvasElement | null = canvasRef.current;

        if (!canvas) {
            return;
        }

        canvas.width = BODY_DIAGRAM_WIDTH * CANVAS_SCALE;
        canvas.height = BODY_DIAGRAM_HEIGHT * CANVAS_SCALE;

        drawStrokes(canvas, strokes);
    }, [strokes]);

    const pointFrom = (event: PointerEvent<HTMLCanvasElement>, canvas: HTMLCanvasElement): Point =>
        clampToDiagram(
            canvasPoint(
                { x: event.clientX, y: event.clientY },
                canvas.getBoundingClientRect(),
                { width: BODY_DIAGRAM_WIDTH, height: BODY_DIAGRAM_HEIGHT }
            )
        );

    /** The pen, at the canvas's scale. Setting `width` resets the context, so every draw asks. */
    const pen = (canvas: HTMLCanvasElement): CanvasRenderingContext2D | null => {
        const context: CanvasRenderingContext2D | null = canvas.getContext('2d');

        if (context) {
            context.lineCap = 'round';
            context.lineJoin = 'round';
            context.strokeStyle = BODY_DIAGRAM_INK;
            context.lineWidth = BODY_DIAGRAM_STROKE_WIDTH * CANVAS_SCALE;
        }

        return context;
    };

    const handlePointerDown = (event: PointerEvent<HTMLCanvasElement>): void => {
        const canvas: HTMLCanvasElement | null = canvasRef.current;

        if (!canvas || currentRef.current !== null) {
            return;
        }

        if (event.pointerType === 'pen') {
            penSeenRef.current = true;
        } else if (event.pointerType === 'touch' && penSeenRef.current) {
            return;
        }

        // Capture, so a stroke that leaves the stage keeps drawing and still ends here.
        // Guarded because jsdom has no pointer capture.
        if (typeof canvas.setPointerCapture === 'function') {
            canvas.setPointerCapture(event.pointerId);
        }

        const point: Point = pointFrom(event, canvas);

        pointerRef.current = event.pointerId;
        currentRef.current = [point];

        // A dot, so a tap on a sore spot leaves a mark.
        const context: CanvasRenderingContext2D | null = pen(canvas);

        if (context) {
            context.beginPath();
            context.moveTo(point.x * CANVAS_SCALE, point.y * CANVAS_SCALE);
            context.lineTo(point.x * CANVAS_SCALE, point.y * CANVAS_SCALE);
            context.stroke();
        }
    };

    /**
     * Draws only the newest segment rather than redrawing the whole picture on every move:
     * a frame of lag between a finger and its ink reads as a broken control. The segment is
     * the same midpoint curve `drawStrokes` produces, so the redraw after the stroke ends
     * lands exactly on top of it.
     */
    const handlePointerMove = (event: PointerEvent<HTMLCanvasElement>): void => {
        const canvas: HTMLCanvasElement | null = canvasRef.current;
        const current: Stroke | null = currentRef.current;

        if (!canvas || !current || event.pointerId !== pointerRef.current) {
            return;
        }

        const point: Point = pointFrom(event, canvas);
        const last: Point = current[current.length - 1];
        const from: Point = current.length > 1 ? midpoint(current[current.length - 2], last) : last;
        const to: Point = midpoint(last, point);

        current.push(point);

        const context: CanvasRenderingContext2D | null = pen(canvas);

        if (context) {
            context.beginPath();
            context.moveTo(from.x * CANVAS_SCALE, from.y * CANVAS_SCALE);
            context.quadraticCurveTo(
                last.x * CANVAS_SCALE,
                last.y * CANVAS_SCALE,
                to.x * CANVAS_SCALE,
                to.y * CANVAS_SCALE
            );
            context.stroke();
        }
    };

    const handlePointerUp = (event: PointerEvent<HTMLCanvasElement>): void => {
        const canvas: HTMLCanvasElement | null = canvasRef.current;
        const current: Stroke | null = currentRef.current;

        if (!canvas || !current || event.pointerId !== pointerRef.current) {
            return;
        }

        if (typeof canvas.hasPointerCapture === 'function' && canvas.hasPointerCapture(event.pointerId)) {
            canvas.releasePointerCapture(event.pointerId);
        }

        currentRef.current = null;
        pointerRef.current = undefined;

        setStrokes((previous) => [...previous, current]);
    };

    const handleUndo = (): void => setStrokes((previous) => previous.slice(0, -1));

    const handleClear = (): void => setStrokes([]);

    const empty: boolean = strokes.length === 0;

    return (
        <>
            <DialogContent ref={areaRef} sx={stageAreaStyles}>
                <Box sx={stageStyles} style={stage ?? undefined}>
                    <Box
                        component="img"
                        src={BODY_DIAGRAM_OUTLINE_SRC}
                        alt={translate(ResourcesIds.EVALUATION_BODY_DIAGRAM_ALT)}
                        draggable={false}
                        sx={outlineImageStyles}
                    />
                    <Box
                        component="canvas"
                        ref={canvasRef}
                        data-testid="body-diagram-canvas"
                        sx={canvasStyles}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onPointerCancel={handlePointerUp}
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={dialogActionsStyles}>
                <Button onClick={handleUndo} disabled={empty}>
                    {translate(ResourcesIds.EVALUATION_BODY_DIAGRAM_UNDO)}
                </Button>
                <Button onClick={handleClear} disabled={empty}>
                    {translate(ResourcesIds.EVALUATION_BODY_DIAGRAM_CLEAR)}
                </Button>
                <Box sx={spacerStyles} />
                <Button onClick={onCancel}>
                    {translate(ResourcesIds.EVALUATION_BODY_DIAGRAM_CANCEL)}
                </Button>
                <Button variant="contained" onClick={(): void => onDone(strokes)}>
                    {translate(ResourcesIds.EVALUATION_BODY_DIAGRAM_DONE)}
                </Button>
            </DialogActions>
        </>
    );
};
